// JAA-388: a blank screen is a client-side JavaScript error, which the backend
// and Azure request logs cannot see. This module sends caught errors to the
// existing Application Insights resource using its plain ingestion endpoint
// (no SDK, so no cookies, no browser storage and no extra bundle weight).
// It is a no-op unless REACT_APP_APPINSIGHTS_CONNECTION_STRING is set at build
// time, so local and unconfigured builds only log to the console.

const MAX_REPORTS_PER_PAGE_LOAD = 10;
const MAX_STEP_EVENTS_PER_PAGE_LOAD = 40;
const DEFAULT_INGESTION_ENDPOINT = 'https://dc.services.visualstudio.com';

const sentKeys = new Set<string>();
let stepEventCount = 0;
// Random per page load and kept in memory only: it links the steps and errors of
// one visit without identifying the person.
const flowId = Math.random().toString(36).slice(2, 10);
const sharedContext: Record<string, string> = {};

type Config = { instrumentationKey: string; endpoint: string };

/**
 * Parses the Application Insights connection string baked into the build.
 * Returns null when it is missing or has no instrumentation key.
 */
function readConfig(): Config | null {
  const raw = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;
  if (!raw) return null;
  const parts = Object.fromEntries(
    raw.split(';').map((pair) => {
      const index = pair.indexOf('=');
      return [pair.slice(0, index).trim(), pair.slice(index + 1).trim()];
    }),
  );
  if (!parts.InstrumentationKey) return null;
  return {
    instrumentationKey: parts.InstrumentationKey,
    endpoint: (parts.IngestionEndpoint || DEFAULT_INGESTION_ENDPOINT).replace(/\/$/, ''),
  };
}

/**
 * Removes personal data from text that is about to leave the browser: email
 * addresses and URL query strings (the login flow puts the email in one).
 */
function scrub(text: string): string {
  return text
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '<email>')
    .replace(/\?[^\s)'"]*/g, '?');
}

/**
 * Adds (or, with null, removes) details that every later report and step event
 * carries, such as which sign-in view is on screen. Values are scrubbed on send.
 */
export function setReportContext(details: Record<string, string | null>): void {
  Object.entries(details).forEach(([key, value]) => {
    if (value === null) delete sharedContext[key];
    else sharedContext[key] = value;
  });
}

/**
 * The properties every telemetry item carries: build, visit id, shared context,
 * browser and the caller's own details, with personal data scrubbed.
 */
function commonProperties(details: Record<string, string>): Record<string, string> {
  const scrubbed = Object.entries({ ...sharedContext, ...details }).map(
    ([key, value]) => [key, scrub(value).slice(0, 2000)],
  );
  return {
    build: process.env.REACT_APP_VERSION || 'dev',
    flowId,
    // Browser page translation marks <html> with translated-ltr / translated-rtl.
    translated: String(/translated-(ltr|rtl)/.test(document.documentElement.className)),
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    ...Object.fromEntries(scrubbed),
  };
}

/**
 * The current route with token-like path segments (reset and claim links, ids)
 * replaced by ":id", so telemetry never carries a secret from the address.
 */
function routeName(): string {
  return window.location.pathname.replace(/\/[A-Za-z0-9_-]{16,}(?=\/|$)/g, '/:id');
}

/**
 * Posts one telemetry item to Application Insights without cookies or storage.
 * The visitor's IP is blanked and the route is the only page detail sent.
 */
function post(config: Config, kind: 'Exception' | 'Event', baseData: Record<string, unknown>): void {
  const envelope = {
    name: `Microsoft.ApplicationInsights.${config.instrumentationKey.replace(/-/g, '')}.${kind}`,
    time: new Date().toISOString(),
    iKey: config.instrumentationKey,
    tags: {
      'ai.cloud.role': 'jaakko-frontend',
      'ai.operation.name': routeName(),
      'ai.location.ip': '0.0.0.0',
    },
    data: { baseType: `${kind}Data`, baseData: { ver: 2, ...baseData } },
  };

  fetch(`${config.endpoint}/v2/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify([envelope]),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Wraps any thrown value in an Error without throwing itself: String() fails on
 * objects without a prototype, and reporting must never become a second crash.
 */
function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  try {
    return new Error(String(value));
  } catch {
    return new Error('Unserializable error value');
  }
}

/**
 * Sends one caught error to Application Insights. Never throws and never
 * blocks the UI: reporting is best-effort, the app has already recovered.
 * Repeats of the same error and floods are dropped client-side.
 */
export function reportError(
  error: unknown,
  context: Record<string, string> = {},
): void {
  const err = toError(error);
  console.error('Reported error:', scrub(err.stack || err.message), context);
  const config = readConfig();
  if (!config || sentKeys.size >= MAX_REPORTS_PER_PAGE_LOAD) return;

  try {
    const stack = scrub(err.stack || '');
    const dedupeKey = `${err.message}|${stack.split('\n')[1] || ''}`;
    if (sentKeys.has(dedupeKey)) return;
    sentKeys.add(dedupeKey);

    post(config, 'Exception', {
      severityLevel: 3,
      exceptions: [
        {
          typeName: err.name,
          message: scrub(err.message).slice(0, 1000),
          hasFullStack: Boolean(err.stack),
          stack: stack.slice(0, 8000),
        },
      ],
      properties: commonProperties(context),
    });
  } catch {
    // reporting must never become a second crash
  }
}

/**
 * Records that the visitor reached a step of a flow (for example the sign-in
 * password view). Names and fixed values only, never what the person typed.
 * Paired with errors through the same visit id, so a visit that reaches a step
 * and then crashes or stops shows up in Application Insights.
 */
export function trackStep(name: string, details: Record<string, string> = {}): void {
  const config = readConfig();
  if (!config || stepEventCount >= MAX_STEP_EVENTS_PER_PAGE_LOAD) return;
  stepEventCount += 1;
  try {
    post(config, 'Event', { name, properties: commonProperties(details) });
  } catch {
    // reporting must never become a second crash
  }
}

/**
 * Reports uncaught errors and unhandled promise rejections that come from this
 * app's own bundles. Errors from third-party scripts (maps, cookie consent)
 * are skipped: they are noisy and cannot be fixed here. Installed when this
 * module is first imported (index.tsx imports it before everything else), so a
 * crash while the rest of the app is still loading is reported too.
 */
function installGlobalErrorReporting(): void {
  const isOurs = (location?: string) => !!location && location.startsWith(window.location.origin);

  window.addEventListener('error', (event) => {
    if (!isOurs(event.filename)) return;
    reportError(event.error ?? new Error(event.message), { source: 'window.onerror' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (!(reason instanceof Error) || !reason.stack?.includes(window.location.origin)) return;
    reportError(reason, { source: 'unhandledrejection' });
  });
}

installGlobalErrorReporting();
